import { Dialog } from 'primereact/dialog';
import { FC, useEffect, useState, useTransition } from 'react';
import { PreloadedQuery, usePreloadedQuery } from 'react-relay';
import { SkillsLessonByTypeQuery } from '../../graphQLOperations/__generated__/SkillsLessonByTypeQuery.graphql';
import { Divider } from 'primereact/divider';
import { getLessonsInfo, LessonInfo } from '../../businessLogicService/getLessonsInfo';
import { CustomBranding } from '../../models/User';
import { disableKhanForMiami, isSomeVideoNotKhan } from '../../businessLogicService/customBrandHelper';
import { Link } from 'react-router-dom';
import AdditionalResourceStuckIcon from '../SkillResource/AdditionalResourceStuckIcon';
import { getLessonsByType } from '../../graphQLOperations/Skills';
import { ThreeDotLoader } from '../Fallback/ThreeDotsLoader';

interface Props {
  visible: boolean;
  setVisible: (s: boolean) => void;
  customBranding: CustomBranding | undefined;
  additionalLessonsQueryRef: PreloadedQuery<SkillsLessonByTypeQuery, Record<string, unknown>>;
}

export const LessonsModal: FC<Props> = ({ visible, setVisible, customBranding, additionalLessonsQueryRef }) => {
  const [loading, startTransition] = useTransition();

  const [lessonInfo, setLessonsInfo] = useState<LessonInfo[]>([]);
  const [nothingToShow, setNothingToShow] = useState<boolean>(false);

  const lessonSkills = usePreloadedQuery<SkillsLessonByTypeQuery>(
    getLessonsByType,
    additionalLessonsQueryRef as PreloadedQuery<SkillsLessonByTypeQuery>,
  );

  useEffect(() => {
    setLessonsInfo(getLessonsInfo(lessonSkills));
  }, [lessonSkills]);

  useEffect(() => {
    startTransition(() => {
      return setNothingToShow(
        lessonInfo.length === 0 || lessonInfo.every(item => !isSomeVideoNotKhan(customBranding, item.lessons)),
      );
    });
  }, [lessonInfo]);

  const header = (
    <div className="tw-flex tw-flex-row tw-gap-2">
      <AdditionalResourceStuckIcon size={30} />{' '}
      <h1 className="tw-font-atkinson tw-text-xl tw-font-bold tw-text-neutral-950">Additional Lessons and Resources</h1>
    </div>
  );
  return (
    <Dialog
      header={header}
      className="nc_modal-wide"
      visible={visible}
      onHide={() => setVisible(false)}
      ariaCloseIconLabel="Close dialog"
    >
      {loading && <ThreeDotLoader dark />}
      <div className="tw-w-full tw-flex tw-flex-col tw-gap-lg">
        {!nothingToShow ? (
          lessonInfo.map((item, index) => {
            if (!item.lessons.length || !isSomeVideoNotKhan(customBranding, item.lessons)) return null;
            return (
              <section
                key={index}
                aria-labelledby={`lessons-section-${index}`}
                className="tw-flex tw-flex-col !tw-pb-0 nc_content-frame"
              >
                <h3 id={`lessons-${index}`} tabIndex={0} className="tw-font-atkinson tw-font-bold tw-text-base">
                  {item.title}
                </h3>
                <ul className="tw-my-0 tw-py-0 !tw-pl-4 tw-list-none">
                  {item.lessons.map(lesson => {
                    if (lesson.teacherOnly || disableKhanForMiami(customBranding, lesson.link)) return null;
                    return (
                      <li key={lesson.id} className="tw-mb-3">
                        <div className="tw-w-9/12 tw-flex tw-flex-col">
                          {lesson.link ? (
                            <Link
                              className="tw-font-atkinson tw-cursor-pointer tw-font-bold tw-text-blue-500 tw-text-sm"
                              to={lesson.link}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {lesson.name}
                            </Link>
                          ) : (
                            lesson.name
                          )}
                          <span className="tw-font-atkinson tw-font-normal tw-text-neutral-700 tw-text-sm">
                            {lesson.description}
                          </span>
                        </div>
                      </li>
                    );
                  })}
                </ul>
                {lessonInfo.length !== index + 1 ? (
                  <Divider align="left" className="tw-my-0 tw-py-0" pt={{ root: { 'aria-hidden': 'true' } }} />
                ) : (
                  <></>
                )}
              </section>
            );
          })
        ) : (
          <div className="tw-flex tw-items-center tw-justify-center tw-p-5">
            <h3 className="tw-font-atkinson tw-text-xl tw-font-normal tw-p-[35px]">There are no resources to show</h3>
          </div>
        )}
      </div>
    </Dialog>
  );
};
